import React, { useState } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { motion, AnimatePresence } from 'framer-motion';

interface FollowUpSuggestionsProps {
  suggestions: string[];
  onSuggestionClick: (question: string) => void;
  onDismiss?: () => void;
}

const MAX_SUGGESTIONS = 4;

const FollowUpSuggestions: React.FC<FollowUpSuggestionsProps> = ({ suggestions, onSuggestionClick, onDismiss }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!suggestions || suggestions.length === 0) return null;

  const displayedSuggestions = suggestions.slice(0, MAX_SUGGESTIONS);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="relative"
      role="group"
      aria-label="Follow-up suggestions"
    >
      {/* Suggestions area */}
      <div className="flex flex-wrap items-center gap-2 pr-10">
        <AnimatePresence mode="popLayout">
          {displayedSuggestions.map((suggestion, index) => (
            <motion.div
              key={suggestion}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25, delay: index * 0.08, ease: 'easeOut' }}
              layout
            >
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm text-left cursor-pointer transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:outline-none"
                style={{
                  color: 'var(--color-text)',
                  backgroundColor: hoveredIndex === index ? 'var(--color-accent)' : 'var(--color-secondary)',
                  borderColor: 'var(--color-accent)',
                }}
                onClick={() => onSuggestionClick(suggestion)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                aria-label={`Ask: ${suggestion}`}
              >
                <span className="line-clamp-1">{suggestion}</span>
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {/* Dismiss button positioned at bottom-right */}
      {onDismiss && (
        <div className="absolute bottom-0 right-0">
          <button
            className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-150 hover:bg-[var(--color-secondary)] focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:outline-none"
            style={{ color: 'var(--color-text)', opacity: 0.5 }}
            onClick={onDismiss}
            aria-label="Dismiss follow-up suggestions"
            title="Dismiss"
          >
            <Cross2Icon className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default FollowUpSuggestions;
